import { ApiListRelationship, ApiModel, ApiResource } from 'lib/interfaces/api';
import { Permission } from 'lib/permissions';

export enum RoleStatus {
  Active = 'active',
  Deactivated = 'deactivated',
}

// NOTE: This should be kept in sync with back-end app/assets/shared/api/internal/dashboard/role_nav_items.json
export enum NavItem {
  Account = 'account',
  AccountHeader = 'account.header',
  AccountStatuses = 'account.statuses',
  AccountTypes = 'account.types',
  Api = 'api',
  ApiAnalytics = 'api-analytics',
  ApiHeader = 'api.header',
  ApiKey = 'api-key',
  ApiLogs = 'api-logs',
  Billing = 'billing',
  Case = 'case',
  CaseAction = 'case.action',
  CaseAnalytics = 'case.analytics',
  CaseAssignmentPolicies = 'case.assignment-policies',
  CaseHeader = 'case.header',
  CaseQueues = 'case.queues',
  CaseSAR = 'case.sar',
  CaseTemplate = 'case.template',
  CoverageMap = 'coverage-map',
  DataHeader = 'data.header',
  Development = 'development',
  DocumentationHeader = 'documentation.header',
  EventsHeader = 'events.header',
  Events = 'events',
  Exporter = 'exporter',
  ExporterHeader = 'exporter.header',
  Graph = 'graph',
  GraphConfig = 'graph.config',
  GraphHeader = 'graph.header',
  GraphTemplates = 'graph.templates',
  GraphV1 = 'graph.v1',
  GraphV1Header = 'graph.v1.header',
  Guides = 'guides',
  HelpCenter = 'help-center',
  Home = 'home',
  HomeHeader = 'home.header',
  Importer = 'importer',
  ImporterHeader = 'importer.header',
  Inquiry = 'inquiry',
  InquiryAnalytics = 'inquiry.analytics',
  InquiryDomainManager = 'inquiry.domain-manager',
  InquiryHeader = 'inquiry.header',
  InquiryTemplate = 'inquiry.template',
  InquiryThemeEditor = 'inquiry.theme-editor',
  InquiryThemeSet = 'inquiry.theme-set',
  InsightQuery = 'insight-query',
  InsightQueryTool = 'insight-query-tool',
  IntegrationLogs = 'integration-logs',
  Integrations = 'integrations',
  IntegrationsHeader = 'integrations.header',
  ListHeader = 'list.header',
  Lists = 'lists',
  Marketplace = 'marketplace',
  MarketplaceHeader = 'marketplace.header',
  Organization = 'organization',
  OrganizationHeader = 'organization.header',
  RateLimits = 'rate-limits',
  Reference = 'reference',
  Report = 'report',
  ReportAnalytics = 'report.analytics',
  ReportHeader = 'report.header',
  ReportTemplate = 'report.template',
  Roles = 'roles',
  Security = 'security',
  SystemStatus = 'system-status',
  Team = 'team',
  TeamHeader = 'team.header',
  Teams = 'teams',
  TransactionAnalytics = 'transaction.analytics',
  TransactionHeader = 'transaction.header',
  Transactions = 'transactions',
  TransactionTypes = 'transaction.types',
  UserAuditLogs = 'user.audit.logs',
  Users = 'users',
  UserSessions = 'user.sessions',
  Verification = 'verification',
  VerificationAnalytics = 'verification.analytics',
  VerificationChecks = 'verification.checks',
  VerificationHeader = 'verification.header',
  VerificationTemplate = 'verification.template',
  WebhookHeader = 'webhook.header',
  Webhook = 'webhook',
  WebhookEvent = 'webhook-event',
  Workflow = 'workflow',
  WorkflowAnalytics = 'workflow.analytics',
  WorkflowHeader = 'workflow.header',
  WorkflowRun = 'workflow.run',
  WorkflowVersion = 'workflow.version',
  WorkflowRuns = 'workflow-runs',
}

interface Preferences {
  'enabled-nav-items': NavItem[];
  'default-landing-page': string;
}

export interface ScopedPermission {
  name: Permission;
  'object-id'?: string;
}

export interface RolePermission {
  name: Permission;
  'environment-id'?: string | null;
  'object-id'?: string | null;
}

export interface RoleAttributes {
  description?: string;
  'role-permissions': RolePermission[];
  preferences: Preferences;
  status?: RoleStatus;
  name: string;
  'deactivated-at'?: string;
  'created-at'?: string;
  'updated-at'?: string;
}

export interface RoleRelationships {
  users: ApiListRelationship<ApiModel.User>;
}

export type Role = ApiResource<RoleAttributes, RoleRelationships, ApiModel.Role>;
